import { useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Loader2, ArrowUpRight, CheckCircle2 } from "lucide-react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { api, formatApiError } from "../../lib/api";
import { FORM_PRIVACY_NOTE, PAYMENT_OPTIONS, SERVICE_OPTIONS } from "../../data/content";

const EMPTY = { name: "", email: "", phone: "", service: "", payment: "", preferred_contact: "Email", message: "" };
const field = "h-12 rounded-xl border-[#D6CFC1] bg-[#FDFBF7] text-base focus-visible:ring-[#C86D51]";

const Label = ({ children, htmlFor }) => (
  <label htmlFor={htmlFor} className="mb-2 block text-sm font-medium text-[#1E2621]">{children}</label>
);

const Pick = ({ id, label, value, onChange, options, placeholder }) => (
  <div>
    <Label htmlFor={id}>{label}</Label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger id={id} className={field} data-testid={`${id}-select`}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o} value={o} data-testid={`${id}-option-${o.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{o}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

export const ContactForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.service || !form.payment) {
      toast.error("Please choose a service and how you plan to pay.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      setForm(EMPTY);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div className="flex min-h-[420px] flex-col items-start justify-center rounded-[2rem] border border-[#E2DCD0] bg-[#FAF8F4] p-8 sm:p-12" data-testid="contact-success">
        <CheckCircle2 className="h-10 w-10 text-[#5E7259]" />
        <h3 className="font-serif-display mt-6 text-4xl">Thank you. We'll be in touch.</h3>
        <p className="mt-3 max-w-md text-[#4A554D]">Someone from our team will reach out soon to talk through next steps and your options.</p>
        <button onClick={() => setSent(false)} className="mt-8 text-sm font-medium underline decoration-[#C86D51] underline-offset-8" data-testid="contact-send-another-button">Send another message</button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-[2rem] border border-[#E2DCD0] bg-[#FAF8F4] p-6 sm:p-10" data-testid="contact-form">
      <div className="mb-8 flex gap-3 rounded-2xl border border-[#F3DBBA] bg-[#FFF8F0] p-4 text-sm leading-relaxed text-[#6E4A1E]" data-testid="contact-privacy-note">
        <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-[#8A5416]" />
        <p>{FORM_PRIVACY_NOTE}</p>
      </div>
      <div className="grid gap-6 sm:grid-cols-2">
        <div className="sm:col-span-2"><Label htmlFor="name">Your name</Label><Input id="name" required value={form.name} onChange={set("name")} className={field} data-testid="contact-name-input" /></div>
        <div><Label htmlFor="email">Email</Label><Input id="email" type="email" required value={form.email} onChange={set("email")} className={field} data-testid="contact-email-input" /></div>
        <div><Label htmlFor="phone">Phone (optional)</Label><Input id="phone" type="tel" value={form.phone} onChange={set("phone")} className={field} data-testid="contact-phone-input" /></div>
        <Pick id="contact-service" label="Service you're interested in" value={form.service} onChange={set("service")} options={SERVICE_OPTIONS} placeholder="Choose a service" />
        <Pick id="contact-payment" label="How do you plan to pay?" value={form.payment} onChange={set("payment")} options={PAYMENT_OPTIONS} placeholder="Choose an option" />
        <Pick id="contact-preferred" label="Best way to reach you" value={form.preferred_contact} onChange={set("preferred_contact")} options={["Email", "Phone"]} placeholder="Email" />
        <div className="sm:col-span-2">
          <Label htmlFor="message">Anything else? (optional)</Label>
          <Textarea id="message" rows={4} value={form.message} onChange={set("message")} placeholder="A good time to call, or a general question. Please no health details." className="rounded-xl border-[#D6CFC1] bg-[#FDFBF7] text-base focus-visible:ring-[#C86D51]" data-testid="contact-message-input" />
        </div>
      </div>
      <button type="submit" disabled={busy} className="group mt-8 inline-flex w-full items-center justify-between rounded-full bg-[#1E2621] py-2 pl-7 pr-2 text-sm font-medium text-[#F4F1EA] transition-colors hover:bg-[#A9533A] disabled:opacity-60 sm:w-auto sm:gap-6" data-testid="contact-submit-button">
        {busy ? "Sending…" : "Send message"}
        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#C86D51] transition-transform duration-500 group-hover:rotate-45">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUpRight className="h-4 w-4" />}
        </span>
      </button>
    </form>
  );
};
